import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { CommonResponse } from '../common/response/response';
import { SnackBarService } from '../shared/services/snackbar.service';
import { NavigationUrl } from '../constant/navigation-url';
import sessionStorageUtility from '../common/utils/sessionStorage';

@Injectable({
    providedIn: 'root',
})
export class HttpService {
    constructor(
        private router: Router,
        private http: HttpClient,
        private snackbarService: SnackBarService
    ) {}

    get<T extends CommonResponse>(path: string, handleErrInSpecComponent = false): Promise<T> {
        return lastValueFrom(this.http.get(path))
            .then((response: object) => {
                const proResponse = response as T;
                if (proResponse.hasError) {
                    if (this.handleIsTokenValid(proResponse)) {
                        return Promise.reject(new Error(''));
                    }
                    if (!handleErrInSpecComponent) {
                        this.snackbarService.errorSnackBar(proResponse.message);
                        return Promise.reject(new Error(proResponse.message));
                    }
                }
                this.setOrUpdateToken(proResponse);
                return Promise.resolve(proResponse);
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(httpErrorResponse);
                return Promise.reject(new Error('Something went wrong'));
            });
    }

    post<T extends CommonResponse>(path: string, data: object, handleErrInSpecComponent = false): Promise<T> {
        return lastValueFrom(this.http.post(path, data))
            .then((response: object) => {
                const proResponse = response as T;
                if (proResponse.hasError) {
                    if (this.handleIsTokenValid(proResponse)) {
                        return Promise.reject(new Error(''));
                    }
                    if (!handleErrInSpecComponent) {
                        this.snackbarService.errorSnackBar(proResponse.message);
                        return Promise.reject(new Error(proResponse.message));
                    }
                }
                this.setOrUpdateToken(proResponse);
                return Promise.resolve(proResponse);
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(httpErrorResponse);
                return Promise.reject(new Error('Something went wrong'));
            });
    }

    put<T extends CommonResponse>(path: string, data: object, handleErrInSpecComponent = false): Promise<T> {
        return lastValueFrom(this.http.put(path, data))
            .then((response: object) => {
                const proResponse = response as T;
                if (proResponse.hasError) {
                    if (this.handleIsTokenValid(proResponse)) {
                        return Promise.reject(new Error(''));
                    }
                    if (!handleErrInSpecComponent) {
                        this.snackbarService.errorSnackBar(proResponse.message);
                        return Promise.reject(new Error(proResponse.message));
                    }
                }
                this.setOrUpdateToken(proResponse);
                return Promise.resolve(proResponse);
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(httpErrorResponse);
                return Promise.reject(new Error('Something went wrong'));
            });
    }

    getAuth<T extends CommonResponse>(path: string, handleErrInSpecComponent = false, responseType: any = 'json'): Promise<T> {
        return lastValueFrom(this.http.get(path, { headers: this.getHeader(), responseType }))
            .then((response: object) => {
                const proResponse = response as T;
                if (proResponse.hasError) {
                    if (this.handleIsTokenValid(proResponse)) {
                        return Promise.reject(new Error(''));
                    }
                    if (!handleErrInSpecComponent) {
                        this.snackbarService.errorSnackBar(proResponse.message);
                        return Promise.reject(new Error(proResponse.message));
                    }
                }
                this.setOrUpdateToken(proResponse);
                return Promise.resolve(proResponse);
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(httpErrorResponse);
                return Promise.reject(new Error('Something went wrong'));
            });
    }

    deleteAuth<T extends CommonResponse>(path: string, data: object, handleErrInSpecComponent = false): Promise<T> {
        return lastValueFrom(this.http.delete(path, { headers: this.getHeader(), body: data }))
            .then((response: object) => {
                const proResponse = response as T;
                if (proResponse.hasError) {
                    if (this.handleIsTokenValid(proResponse)) {
                        return Promise.reject(new Error(''));
                    }
                    if (!handleErrInSpecComponent) {
                        this.snackbarService.errorSnackBar(proResponse.message);
                        return Promise.reject(new Error(proResponse.message));
                    }
                }
                this.setOrUpdateToken(proResponse);
                return Promise.resolve(proResponse);
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(httpErrorResponse);
                return Promise.reject(new Error('Something went wrong'));
            });
    }

    postAuth<T extends CommonResponse>(
        path: string,
        data: object,
        handleErrInSpecComponent = false,
        responseType: any = 'json'
    ): Promise<T> {
        return lastValueFrom(this.http.post(path, data, { headers: this.getHeader(), responseType }))
            .then((response: object) => {
                const proResponse = response as T;
                if (proResponse.hasError) {
                    if (this.handleIsTokenValid(proResponse)) {
                        return Promise.reject(new Error(''));
                    }
                    if (!handleErrInSpecComponent) {
                        this.snackbarService.errorSnackBar(proResponse.message);
                        return Promise.reject(new Error(proResponse.message));
                    }
                }
                this.setOrUpdateToken(proResponse);
                return Promise.resolve(proResponse);
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(httpErrorResponse);
                return Promise.reject(new Error('Something went wrong'));
            });
    }

    putAuth<T extends CommonResponse>(path: string, data: object = {}, handleErrInSpecComponent = false): Promise<T> {
        return lastValueFrom(this.http.put(path, data, { headers: this.getHeader() }))
            .then((response: object) => {
                const proResponse = response as T;
                if (proResponse.hasError && !handleErrInSpecComponent) {
                    if (this.handleIsTokenValid(proResponse)) {
                        return Promise.reject(new Error(''));
                    }
                    if (!handleErrInSpecComponent) {
                        this.snackbarService.errorSnackBar(proResponse.message);
                        return Promise.reject(new Error(proResponse.message));
                    }
                }
                this.setOrUpdateToken(proResponse);
                return Promise.resolve(proResponse);
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(httpErrorResponse);
                return Promise.reject(new Error('Something went wrong'));
            });
    }

    private handleHttpErrorResponse(httpErrorResponse: HttpErrorResponse): void {
        if (!httpErrorResponse.error) {
            return;
        }
        switch (httpErrorResponse.status) {
            case 500:
                this.router.navigateByUrl(NavigationUrl.INTERNAL_SERVER_ERROR);
                break;
            case 404:
                this.router.navigateByUrl(NavigationUrl.PAGE_NOT_FOUND);
                break;
            case 401:
                this.router.navigateByUrl(NavigationUrl.UNAUTHORIZED);
                break;
            case 403:
                this.router.navigateByUrl(NavigationUrl.FORBIDDEN);
                break;
            case 502:
                this.router.navigateByUrl(NavigationUrl.BAD_GATEWAY);
                break;
            case 503:
                this.router.navigateByUrl(NavigationUrl.SERVICE_UNAVAILABLE);
                break;
            case 504:
                this.router.navigateByUrl(NavigationUrl.API_GATEWAY_TIMED_OUT);
                break;
            case 400:
                this.router.navigateByUrl(NavigationUrl.BAD_REQUEST);
                break;
            case 0:
                this.router.navigateByUrl(NavigationUrl.ERROR);
                if (sessionStorageUtility.getAccessToken()) {
                } else {
                    this.router.navigateByUrl(NavigationUrl.INVALID_DOMAIN_URL);
                }
                break;
            default:
                this.snackbarService.errorSnackBar(httpErrorResponse.message);
                break;
        }
    }

    private handleIsTokenValid(response: CommonResponse) {
        if (response.code === 2009) {
            this.snackbarService.errorSnackBar('Token is Invalid. Please Login in again');
            sessionStorage.clear();
            this.router.navigateByUrl(NavigationUrl.LOGIN);
            return true;
        } else if (response.code === 2007) {
            this.snackbarService.errorSnackBar('You are unauthoried to access the request resource');
            sessionStorage.clear();
            this.router.navigateByUrl(NavigationUrl.LOGIN);
            return true;
        }
        return false;
    }

    private setOrUpdateToken(response: CommonResponse): void {
        if (response.access_token) {
            sessionStorageUtility.setAccessToken(response.access_token);
        }
        if (response.refresh_token) {
            sessionStorageUtility.setRefreshToken(response.refresh_token);
        }
    }

    private getHeader() {
        return new HttpHeaders({
            Authorization: 'Bearer ' + sessionStorageUtility.getAccessToken(),
        });
    }
}
